import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CalendarClock, Check, X, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  proposed: { text: "Bekliyor", cls: "bg-warning/10 text-warning" },
  accepted: { text: "Onayladın", cls: "bg-success/10 text-success" },
  rejected: { text: "Reddettin", cls: "bg-destructive/10 text-destructive" },
  rescheduled: { text: "Alternatif önerdin", cls: "bg-primary/10 text-primary" },
  cancelled: { text: "İptal", cls: "bg-muted text-muted-foreground" },
  completed: { text: "Tamamlandı", cls: "bg-accent/10 text-accent" },
};

export default function ParentMeetings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`${V1}/meetings/mine`);
      setItems(r.data || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const respond = async (id, action, slot_index) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.post(`${V1}/meetings/${id}/respond`, { action, slot_index });
      toast.success(action === "accept" ? "Görüşme onaylandı" : action === "reject" ? "Reddedildi" : "Kaydedildi");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  if (loading) return <div className="text-muted-foreground">Yükleniyor…</div>;

  return (
    <div className="space-y-6" data-testid="parent-meetings-page">
      <div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><CalendarClock className="h-7 w-7 text-primary" /> Görüşmelerim</h1>
        <p className="text-muted-foreground text-sm mt-1">Okulların önerdiği mülakat/randevu tarihlerini görüntüle, uygun olan saati seç veya reddet.</p>
      </div>

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="p-10 rounded-xl border border-dashed border-border text-center text-muted-foreground">
            Henüz bir görüşme daveti yok.
          </div>
        )}
        {items.map((m) => {
          const st = STATUS_LABEL[m.status] || { text: m.status, cls: "bg-muted" };
          const pending = m.status === "proposed" || m.status === "rescheduled";
          return (
            <div key={m.id} className="p-4 rounded-xl border border-border bg-card" data-testid={`p-meeting-${m.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium">{m.school_name || "Okul"}</div>
                    <span className={`text-xs px-2 py-0.5 rounded ${st.cls}`}>{st.text}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{m.child_name || "Öğrenci"} · {new Date(m.created_at).toLocaleString("tr-TR")}</div>
                  {m.message && <div className="text-xs mt-2 italic text-muted-foreground">“{m.message}”</div>}
                </div>
              </div>
              <div className="mt-3 grid gap-2">
                {(m.proposed_slots || []).map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded border border-border bg-background/60" data-testid={`p-meeting-slot-${m.id}-${i}`}>
                    <div className="text-sm">
                      <div className="font-medium flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(s.start).toLocaleString("tr-TR")}</div>
                      <div className="text-xs text-muted-foreground">Bitiş: {new Date(s.end).toLocaleString("tr-TR")} · {s.meeting_type === "video" ? "Online" : s.meeting_type === "phone" ? "Telefon" : "Yüz yüze"}{s.location ? ` · ${s.location}` : ""}</div>
                    </div>
                    {pending && (
                      <Button size="sm" onClick={() => respond(m.id, "accept", i)} disabled={busy[m.id]} data-testid={`p-meeting-accept-${m.id}-${i}`}>
                        <Check className="h-3.5 w-3.5" /> Bu saati seç
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              {pending && (
                <div className="mt-3">
                  <Button variant="outline" size="sm" onClick={() => respond(m.id, "reject")} disabled={busy[m.id]} data-testid={`p-meeting-reject-${m.id}`}>
                    <X className="h-3.5 w-3.5" /> Görüşmeyi Reddet
                  </Button>
                </div>
              )}
              {m.selected_slot && (
                <div className="mt-3 text-xs text-success">
                  ✅ Onaylı: {new Date(m.selected_slot.start).toLocaleString("tr-TR")} — {m.selected_slot.location || (m.selected_slot.meeting_type === "video" ? "Online" : "")}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
